import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from '@/app/config/env';
import { tokenStorage } from '@/services/storage/tokenStorage';
import { AppError, categorizeHttpError, errorMeta } from '@/services/errors/AppError';

interface RetryableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface RefreshJwtResponseData {
  jwtToken?: string;
  refreshToken?: string;
}

export interface ApiResponse<T = unknown> {
  success: 'true' | 'false';
  Resp_code: 'RCS' | 'ERR';
  Resp_desc: string;
  data?: T;
  recordsTotal?: number;
  recordsFiltered?: number;
}

export interface TableParams {
  page?: number;
  start?: number;
  length?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  [key: string]: unknown;
}

let _onUnauthenticated: (() => void) | null = null;

export function registerUnauthenticatedHandler(handler: () => void) {
  _onUnauthenticated = handler;
}

const client = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(config => {
  const token = tokenStorage.getToken();
  if (token) {
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

// Shared in-flight refresh promise so concurrent 401s trigger only one refresh call.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  const user = tokenStorage.getUser<{ email?: string }>();
  const username = user?.email;

  if (!refreshToken || !username) {
    throw new Error('No refresh token available');
  }

  const refreshUrl = env.API_BASE_URL.endsWith('/')
    ? `${env.API_BASE_URL}api/v1/auth/refresh`
    : `${env.API_BASE_URL}/api/v1/auth/refresh`;

  const res = await axios.post<{ success?: boolean; data?: RefreshJwtResponseData }>(
    refreshUrl,
    { refreshToken, username },
  );

  const newToken = res.data?.data?.jwtToken;
  const newRefreshToken = res.data?.data?.refreshToken;

  if (!newToken) {
    throw new Error('Refresh response did not include a new token');
  }

  tokenStorage.setToken(newToken);
  if (newRefreshToken) {
    tokenStorage.setRefreshToken(newRefreshToken);
  }
  return newToken;
}

client.interceptors.response.use(
  (response: AxiosResponse) => response,
  async error => {
    if (axios.isAxiosError(error)) {
      if (!error.response) {
        const isTimeout = error.code === 'ECONNABORTED';
        throw new AppError(
          isTimeout ? 'Timeout' : 'NetworkError',
          isTimeout ? 'The request timed out. Please check your connection.' : 'Unable to reach the server. Check your network connection.',
        );
      }

      const { status, data } = error.response;
      const originalRequest = error.config as RetryableRequestConfig | undefined;

      if (status === 401) {
        const requestUrl = originalRequest?.url || '';
        const isAuthEndpoint =
          requestUrl.includes('api/v1/auth/login') ||
          requestUrl.includes('api/v1/auth/refresh') ||
          requestUrl.includes('api/v1/auth/logout');

        // 1. Do not trigger session reset or token refresh for login, refresh, or logout endpoints
        if (isAuthEndpoint) {
          throw categorizeHttpError(status, data, errorMeta(error.response.headers, data));
        }

        // 2. If this request was already retried once after token refresh,
        // it means the token is fresh, but this specific endpoint returned 401 (permission/role restriction).
        // DO NOT clear session or redirect to /login!
        if (originalRequest?._retry) {
          throw categorizeHttpError(status, data, errorMeta(error.response.headers, data));
        }

        // 3. Attempt token refresh if a refresh token is stored
        const refreshToken = tokenStorage.getRefreshToken();
        if (refreshToken && originalRequest) {
          originalRequest._retry = true;
          try {
            if (!refreshPromise) {
              refreshPromise = refreshAccessToken().finally(() => {
                refreshPromise = null;
              });
            }
            const newToken = await refreshPromise;

            if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            } else {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return client.request(originalRequest);
          } catch {
            // Refresh token itself failed (expired/invalid/revoked) -> session is dead
            tokenStorage.clear();
            _onUnauthenticated?.();
            throw categorizeHttpError(status, data, errorMeta(error.response.headers, data));
          }
        }

        // 4. No refresh token available -> session is unauthenticated
        tokenStorage.clear();
        _onUnauthenticated?.();
      }

      throw categorizeHttpError(status, data, errorMeta(error.response.headers, data));
    }
    throw error;
  },
);

async function get<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
  const res = await client.get<ApiResponse<T>>(url, { params });
  return res.data;
}

async function post<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await client.post<ApiResponse<T>>(url, body);
  return res.data;
}

async function put<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await client.put<ApiResponse<T>>(url, body);
  return res.data;
}

async function del<T>(url: string): Promise<ApiResponse<T>> {
  const res = await client.delete<ApiResponse<T>>(url);
  return res.data;
}

async function uploadFile<T>(
  url: string,
  formData: FormData,
  onUploadProgress?: (percent: number) => void,
): Promise<ApiResponse<T>> {
  const res = await client.post<ApiResponse<T>>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => {
      if (onUploadProgress && e.total) {
        onUploadProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  });
  return res.data;
}

async function downloadFile(url: string, params?: Record<string, unknown>): Promise<Blob> {
  const res = await client.get(url, { params, responseType: 'blob' });
  return res.data as Blob;
}

function rawRequest<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
  return client.request<T>(config);
}

export const apiClient = { get, post, put, del, uploadFile, downloadFile, rawRequest };
